import { useEffect } from "react";
import { useSelector } from "react-redux";
import { getReload } from "reduxModules/ducks/ui";
import isBoolean from "lodash/isBoolean";
import { RootState } from "types/redux/state";

/**
 * Custom hook to update URL parameters based on the savekey and revision in Redux state.
 * 
 * This hook:
 * 1. Gets the current savekey, revision, and reload state from Redux
 * 2. Constructs a new URL with these parameters
 * 3. Updates the browser history with the new URL without causing a page reload
 * 4. Removes the version parameter if reload is true
 * 
 * @returns void
 */
const useSetURLParams = (): void => {
  // Construct the base URL from the current location
  const baseURL = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
  
  // Get relevant state from Redux
  const savekey = useSelector((state: RootState) => state.ui.savekey);
  const ver = useSelector((state: RootState) => state.ui.revision);
  const reload = useSelector(getReload);
  
  // Create the URL with parameters if savekey exists
  let saveURL: string;
  let searchParams: URLSearchParams | null = null;
  
  if (savekey) {
    searchParams = new URLSearchParams({ savekey, ver });
    
    // Remove the version parameter if reload is true
    if (isBoolean(reload) && reload) {
      searchParams.delete('ver');
    }
    
    saveURL = `${baseURL}?${searchParams}`;
  } else {
    saveURL = baseURL;
  }

  // Update the browser history when parameters change
  useEffect(() => {
    window.history.pushState({ path: saveURL }, '', saveURL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams?.toString()]);
};

export default useSetURLParams;
